import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowForward,
  PlayCircleOutline,
  VerifiedUser,
  Speed,
  Shield,
  Domain,
  AdminPanelSettings,
  CalendarMonth,
  Devices,
  Block,
  Check,
  RocketLaunch,
  CreditCardOff,
  SupportAgent,
  SwapHoriz,
  Sms as SmsIcon,
  Android,
  Apple,
  Headset,
  FolderShared,
  AutoAwesome,
  Palette,
  Forum,
  Description,
  FilterAlt,
  Backup,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, verifyOtp, isLoading, error, clearError, twoFactorRequired, clearTwoFactor } = useAuthStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>('signin');
  const authRef = useRef<HTMLDivElement>(null);

  // Sign-in form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Check hash on mount
  useEffect(() => {
    if (window.location.hash === '#auth') {
      setTimeout(() => authRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    if (!email || !password) { setLocalError('Please enter both email and password'); return; }
    try {
      const requires2FA = await login(email, password);
      if (!requires2FA) navigate('/mail/inbox');
    } catch { /* store handles */ }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    if (!otp || otp.length !== 6) { setLocalError('Please enter a valid 6-digit code'); return; }
    try {
      await verifyOtp(twoFactorRequired?.email || email, otp);
      navigate('/mail/inbox');
    } catch { /* store handles */ }
  };

  const handleResendOtp = async () => {
    setLocalError(''); clearError();
    try { await login(email, password); } catch { /* store handles */ }
  };

  const scrollToAuth = () => authRef.current?.scrollIntoView({ behavior: 'smooth' });

  const features = [
    { icon: <Domain />, title: 'Custom Domain Email', desc: 'Professional email addresses on your own domain. SPF, DKIM & DMARC built-in for 99.8% inbox delivery.', color: '#dc2626', bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', glow: 'rgba(220,38,38,0.12)' },
    { icon: <Shield />, title: 'Enterprise Security', desc: '256-bit TLS encryption, two-factor authentication, Rspamd spam filtering, and email quarantine management.', color: '#3b82f6', bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', glow: 'rgba(59,130,246,0.12)' },
    { icon: <Headset />, title: 'Built-in Helpdesk', desc: 'Integrated ticketing system for customer support. Manage tickets, queues, SLA, and knowledge base — all inside your mailbox.', color: '#8b5cf6', bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', glow: 'rgba(139,92,246,0.12)' },
    { icon: <Forum />, title: 'Team Chat', desc: 'Real-time domain-level group chat built right into your email. Collaborate without switching apps.', color: '#06b6d4', bg: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)', glow: 'rgba(6,182,212,0.12)' },
    { icon: <Description />, title: 'Docs & Sheets', desc: 'Create and collaborate on documents and spreadsheets directly in CruvzMail. No Google Docs or Office 365 needed.', color: '#22c55e', bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', glow: 'rgba(34,197,94,0.12)' },
    { icon: <Palette />, title: 'Custom Branding', desc: 'White-label your email with your logo, colors, and sidebar branding. Your team sees your brand, not ours.', color: '#ec4899', bg: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)', glow: 'rgba(236,72,153,0.12)' },
    { icon: <AdminPanelSettings />, title: 'Powerful Admin Console', desc: 'Full control over users, domains, security policies, activity logs, and organization settings from one dashboard.', color: '#14b8a6', bg: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', glow: 'rgba(20,184,166,0.12)' },
    { icon: <FilterAlt />, title: 'Smart Filters & Rules', desc: 'Auto-sort, label, forward, or archive emails with powerful filter rules. AI-powered spam detection included.', color: '#f59e0b', bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', glow: 'rgba(245,158,11,0.12)' },
    { icon: <CalendarMonth />, title: 'Calendar, Tasks & Notes', desc: 'Integrated calendar, task manager, notes, and bookmarks — your complete productivity suite in one place.', color: '#eab308', bg: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', glow: 'rgba(234,179,8,0.12)' },
    { icon: <FolderShared />, title: 'File Sharing', desc: 'Share attachments and files securely across your organization. Built-in file manager with drag-and-drop upload.', color: '#6366f1', bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', glow: 'rgba(99,102,241,0.12)' },
    { icon: <Backup />, title: 'Backup & Migration', desc: 'Automated backups and one-click migration from Gmail, Outlook, or any IMAP provider. Never lose an email.', color: '#0ea5e9', bg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', glow: 'rgba(14,165,233,0.12)' },
    { icon: <Block />, title: 'Zero Ads. Zero Tracking.', desc: 'No advertisements, no data mining, no third-party tracking. Your data stays on your infrastructure. Period.', color: '#f97316', bg: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', glow: 'rgba(249,115,22,0.12)' },
  ];

  const whyItems = [
    { title: 'SPF, DKIM & DMARC Built-in', desc: 'Full email authentication ensures your emails always reach the inbox, never spam.' },
    { title: 'Dedicated Infrastructure', desc: 'Your email runs on isolated servers — not shared hosting. Maximum reliability and performance.' },
    { title: 'Team Collaboration', desc: 'Team chat, shared contacts, file sharing, and group email built right into your inbox.' },
    { title: 'Easy Migration', desc: 'Switch from Gmail, Outlook, or any provider with our automated migration tools.' },
  ];

  const metrics = [
    { label: 'Email Deliverability', value: '99.8%', width: 99.8, gradient: 'linear-gradient(90deg,#dc2626,#f87171)' },
    { label: 'Uptime Guarantee', value: '99.9%', width: 99.9, gradient: 'linear-gradient(90deg,#3b82f6,#60a5fa)' },
    { label: 'Spam Block Rate', value: '99.5%', width: 99.5, gradient: 'linear-gradient(90deg,#22c55e,#4ade80)' },
    { label: 'Customer Satisfaction', value: '98%', width: 98, gradient: 'linear-gradient(90deg,#f59e0b,#fbbf24)' },
  ];

  const mockEmails = [
    { initials: 'JD', color: '#3b82f6', from: 'John Davidson', subject: 'Q2 Marketing Report - Please review the attached...', time: '10:32 AM', unread: true },
    { initials: 'SM', color: '#22c55e', from: 'Sarah Miller', subject: 'Re: Project timeline update - Looks good to me!', time: '9:45 AM', unread: true },
    { initials: 'AK', color: '#f59e0b', from: 'Alex Kumar', subject: 'New client onboarding - Schedule a call this week', time: '8:20 AM', unread: true },
    { initials: 'LP', color: '#8b5cf6', from: 'Lisa Park', subject: 'Design mockups ready for review', time: 'Yesterday', unread: false },
    { initials: 'RT', color: '#ec4899', from: 'Ravi Teja', subject: 'Invoice #2847 - Payment confirmation', time: 'Yesterday', unread: false },
    { initials: 'DW', color: '#14b8a6', from: 'Dev Team', subject: 'Sprint retrospective notes - Action items inside', time: 'Mon', unread: false },
    { initials: 'MR', color: '#6366f1', from: 'Mary Roberts', subject: 'Re: Team lunch this Friday?', time: 'Mon', unread: false },
  ];

  const navLinkSx = {
    color: '#4a5568', fontSize: '0.9rem', fontWeight: 500, textDecoration: 'none', cursor: 'pointer',
    position: 'relative' as const,
    '&:hover': { color: '#dc2626' },
    '&::after': {
      content: '""', position: 'absolute' as const, bottom: -4, left: 0, width: 0, height: '2px',
      background: '#dc2626', transition: 'width 0.3s',
    },
    '&:hover::after': { width: '100%' },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px', bgcolor: '#f9fafb',
      '& fieldset': { borderColor: '#e5e7eb', borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#dc2626', borderWidth: '1.5px' },
      '&.Mui-focused': { bgcolor: '#fff' },
    },
    '& .MuiInputLabel-root': { color: '#64748b' },
  };

  return (
    <Box sx={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', overflowX: 'hidden' }}>

      {/* ===== NAVBAR ===== */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: { xs: 48, md: 77 } }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: { xs: '52px', md: '88px' }, mt: { xs: -1.5, md: -2.5 }, letterSpacing: '0.02em', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', display: { xs: 'none', sm: 'block' } }}>Where Business Speaks!</Typography>
            </Box>

          {/* Desktop nav links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/enterprise')}>For Business</Typography>
            <Typography component="a" href="#features" sx={navLinkSx}>Features</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/migration')}>Migration</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/signup')}>Plans and Pricing</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/help-center')}>Support</Typography>
          </Box>

          {/* Right: desktop buttons + mobile hamburger */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button onClick={scrollToAuth} sx={{
              px: { xs: 1.5, md: 2.5 }, py: 1, borderRadius: '8px', fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button onClick={() => { setAuthTab('signup'); scrollToAuth(); }} sx={{
              px: { xs: 1.5, md: 2.5 }, py: 1, borderRadius: '8px', fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.25)',
              display: { xs: 'none', sm: 'flex' },
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', transform: 'translateY(-1px)' },
            }}>Get Started Free</Button>
            {/* Hamburger — mobile only */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, color: '#374151', ml: 0.5 }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: 280, pt: 1 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
            <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: 40 }} />
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu"><CloseIcon /></IconButton>
          </Box>
          <Divider />
          <List sx={{ px: 1 }}>
            {[
              { label: 'For Business', action: () => { navigate('/enterprise'); setMobileOpen(false); } },
              { label: 'Features', action: () => { document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); setMobileOpen(false); } },
              { label: 'Migration', action: () => { navigate('/migration'); setMobileOpen(false); } },
              { label: 'Plans & Pricing', action: () => { navigate('/signup'); setMobileOpen(false); } },
              { label: 'Support', action: () => { navigate('/help-center'); setMobileOpen(false); } },
            ].map((item) => (
              <ListItemButton
                key={item.label}
                onClick={item.action}
                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: '#fef2f2' } }}
              >
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500, color: '#374151', fontSize: '0.95rem' }} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button fullWidth onClick={() => { scrollToAuth(); setMobileOpen(false); }} sx={{
              py: 1.25, borderRadius: '8px', fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button fullWidth onClick={() => { setAuthTab('signup'); scrollToAuth(); setMobileOpen(false); }} sx={{
              py: 1.25, borderRadius: '8px', fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
            }}>Get Started Free</Button>
          </Box>
        </Drawer>
      </Box>

      {/* ===== HERO ===== */}
      <Box sx={{
        pt: { xs: '120px', md: '140px' }, pb: { xs: 6, md: 10 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(168deg, #ffffff 0%, #fef7f7 30%, #fdf2f2 50%, #f8faff 100%)',
        position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: 'radial-gradient(circle, rgba(220,38,38,0.06) 0%, transparent 70%)', borderRadius: '50%' },
        '&::after': { content: '""', position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, transparent 70%)', borderRadius: '50%' },
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', alignItems: { xs: 'center', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 7.5 }, position: 'relative', zIndex: 1 }}>
          {/* Hero Text */}
          <Box sx={{ flex: 1, maxWidth: 560 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
              bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 100, mb: 3,
            }}>
              <Box sx={{ width: 6, height: 6, bgcolor: '#dc2626', borderRadius: '50%', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#dc2626' }}>Trusted by businesses worldwide</Typography>
            </Box>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, fontWeight: 800, lineHeight: 1.15, color: '#0f172a', mb: 2.5, letterSpacing: '-0.02em' }}>
              Professional Email<br />for <Box component="span" sx={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Modern Business</Box>
            </Typography>
            <Typography sx={{ fontSize: '1.15rem', lineHeight: 1.7, color: '#64748b', mb: 4.5, maxWidth: 480 }}>
              Secure, fast, and beautifully designed email hosting for your organization.
              Custom domain emails, powerful admin tools, and seamless collaboration — all in one platform.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 1.75 }, mb: 6 }}>
              <Button onClick={() => { setAuthTab('signup'); scrollToAuth(); }} sx={{
                px: 4, py: 1.75, borderRadius: '10px', fontSize: '1rem', fontWeight: 600, textTransform: 'none',
                color: '#fff', bgcolor: '#dc2626', boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
                '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 6px 24px rgba(220,38,38,0.4)', transform: 'translateY(-2px)' },
                transition: 'all 0.25s',
              }} endIcon={<ArrowForward sx={{ fontSize: 18 }} />}>Start Free Trial</Button>
              <Button href="#features" sx={{
                px: 4, py: 1.75, borderRadius: '10px', fontSize: '1rem', fontWeight: 600, textTransform: 'none',
                color: '#374151', bgcolor: '#fff', border: '1.5px solid #e5e7eb',
                '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
                transition: 'all 0.25s',
              }} startIcon={<PlayCircleOutline sx={{ fontSize: 18 }} />}>See Features</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 5 } }}>
              {[{ val: '99.9%', label: 'Uptime SLA' }, { val: '256-bit', label: 'Encryption' }, { val: 'Ad-Free', label: 'Always' }].map((s) => (
                <Box key={s.label}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>{s.val}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Hero Visual — Email App Mockup */}
          <Box sx={{ flex: 1, maxWidth: 640, position: 'relative' }}>
            {/* Floating badges */}
            <Box sx={{
              position: 'absolute', bottom: 60, left: -20, zIndex: 5, display: { xs: 'none', md: 'flex' },
              alignItems: 'center', gap: 1, px: 2.25, py: 1.25, borderRadius: 3, bgcolor: '#fff',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)', fontSize: '0.8rem', fontWeight: 600,
              animation: 'float 3s ease-in-out infinite 0.5s',
              '@keyframes float': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-8px)' } },
            }}>
              <VerifiedUser sx={{ fontSize: 18, color: '#22c55e' }} /> End-to-end encrypted
            </Box>
            <Box sx={{
              position: 'absolute', top: 30, right: -20, zIndex: 5, display: { xs: 'none', md: 'flex' },
              alignItems: 'center', gap: 1, px: 2.25, py: 1.25, borderRadius: 3, bgcolor: '#fff',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)', fontSize: '0.8rem', fontWeight: 600,
              animation: 'float 3s ease-in-out infinite 1s',
            }}>
              <Speed sx={{ fontSize: 18, color: '#3b82f6' }} /> 99.9% Uptime
            </Box>

            <Box sx={{
              borderRadius: 4, overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0,0,0,0.12), 0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}>
              {/* Browser bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                {[{ c: '#ef4444' }, { c: '#f59e0b' }, { c: '#22c55e' }].map((d, i) => (
                  <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.c }} />
                ))}
                <Box sx={{ flex: 1, ml: 1.5, px: 1.75, py: 0.5, bgcolor: '#fff', borderRadius: 1.5, fontSize: '0.75rem', color: '#64748b', border: '1px solid #e5e7eb' }}>
                  cruvzmail.com/mail/inbox
                </Box>
              </Box>

              {/* App mockup */}
              <Box sx={{ display: 'flex', height: 380 }}>
                {/* Sidebar */}
                <Box sx={{ width: 200, bgcolor: '#1e2a3a', py: 1.5, borderRight: '1px solid rgba(255,255,255,0.06)', display: { xs: 'none', sm: 'flex' }, flexDirection: 'column' }}>
                  <Box sx={{ mx: 1.5, mb: 2, px: 1.25, py: 1.25, bgcolor: '#3b82f6', borderRadius: 2, color: '#fff', fontSize: '0.8rem', fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    ✏️ New Mail
                  </Box>
                  {[
                    { label: 'Inbox', icon: '📥', active: true, badge: 3 },
                    { label: 'All unread', icon: '📬' },
                  ].map((item) => (
                    <Box key={item.label} sx={{
                      px: 2, py: 1, fontSize: '0.78rem', color: item.active ? '#fff' : 'rgba(255,255,255,0.6)',
                      bgcolor: item.active ? 'rgba(255,255,255,0.08)' : 'transparent',
                      borderLeft: item.active ? '3px solid #3b82f6' : '3px solid transparent',
                      display: 'flex', alignItems: 'center', gap: 1.25,
                    }}>
                      {item.icon} {item.label}
                      {item.badge && <Box sx={{ ml: 'auto', bgcolor: '#dc2626', color: '#fff', borderRadius: 10, px: 0.875, fontSize: '0.65rem', fontWeight: 700 }}>{item.badge}</Box>}
                    </Box>
                  ))}
                  <Typography sx={{ px: 2, pt: 2, pb: 0.75, fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Folders</Typography>
                  {['📥 Inbox', '📝 Drafts', '📤 Sent', '⚠️ Spam', '🗑️ Trash'].map((f) => (
                    <Box key={f} sx={{ px: 2, py: 1, fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      {f}
                    </Box>
                  ))}
                </Box>

                {/* Main content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3b82f6', borderBottom: '2px solid #3b82f6', pb: 0.25 }}>📧 Mail</Typography>
                    <Box sx={{ px: 1.75, py: 0.5, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 1.5, fontSize: '0.72rem', color: '#94a3b8' }}>🔍 Search mail...</Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Inbox</Typography>
                  </Box>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {mockEmails.map((em, i) => (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', px: 2, py: 1.25, gap: 1.5,
                        borderBottom: '1px solid #f8fafc', '&:hover': { bgcolor: '#fafbfd' },
                      }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: em.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#fff', flexShrink: 0 }}>{em.initials}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: em.unread ? 600 : 500, color: em.unread ? '#0f172a' : '#1e293b' }}>{em.from}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: em.unread ? '#374151' : '#64748b', fontWeight: em.unread ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{em.subject}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', flexShrink: 0 }}>{em.time}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== GET THE APP ===== */}
      <Box sx={{ py: { xs: 6, md: 9 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -80, right: -80, width: 350, height: 350, background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', borderRadius: '50%' },
        '&::after': { content: '""', position: 'absolute', bottom: -60, left: -60, width: 280, height: 280, background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', borderRadius: '50%' },
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 5, md: 8 }, position: 'relative', zIndex: 1 }}>
          {/* Left content */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, bgcolor: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 100, mb: 2.5 }}>
              <Devices sx={{ fontSize: 16, color: '#fca5a5' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#fca5a5' }}>Available on Mobile</Typography>
            </Box>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 800, color: '#fff', mb: 1.5, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Your email, always<br />in your <Box component="span" sx={{ background: 'linear-gradient(135deg, #fca5a5, #dc2626)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>pocket</Box>
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, mb: 3.5, maxWidth: 440, mx: { xs: 'auto', md: 0 } }}>
              Stay connected with your team on the go. Full inbox, compose, contacts, and calendar — right from your phone.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Button
                href="/CruvzMail-v1.0.apk"
                download
                sx={{
                  px: 3.5, py: 1.75, borderRadius: '14px', fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                  color: '#fff', background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  boxShadow: '0 4px 20px rgba(220,38,38,0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(220,38,38,0.45)' },
                  transition: 'all 0.3s', display: 'flex', alignItems: 'center', gap: 1.25,
                }}
                startIcon={<Android sx={{ fontSize: 24 }} />}
              >
                Download for Android
              </Button>
              <Button
                disabled
                sx={{
                  px: 3.5, py: 1.75, borderRadius: '14px', fontSize: '0.95rem', fontWeight: 600, textTransform: 'none',
                  color: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                }}
                startIcon={<Apple sx={{ fontSize: 24 }} />}
              >
                iOS — Coming Soon
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 4, mt: 4, justifyContent: { xs: 'center', md: 'flex-start' } }}>
              {[{ icon: <Shield sx={{ fontSize: 18, color: '#22c55e' }} />, text: 'Secure & encrypted' }, { icon: <Speed sx={{ fontSize: 18, color: '#3b82f6' }} />, text: 'Fast & lightweight' }, { icon: <VerifiedUser sx={{ fontSize: 18, color: '#f59e0b' }} />, text: '11 MB only' }].map((item) => (
                <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {item.icon}
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right — Phone mockup */}
          <Box sx={{ flex: 0, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
              width: { xs: 220, md: 260 }, height: { xs: 440, md: 520 },
              borderRadius: '36px', border: '4px solid rgba(255,255,255,0.15)',
              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
              boxShadow: '0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
              overflow: 'hidden', position: 'relative',
            }}>
              {/* Phone notch */}
              <Box sx={{ width: 80, height: 24, bgcolor: '#0f172a', borderRadius: '0 0 16px 16px', mx: 'auto', position: 'relative', zIndex: 2 }} />
              {/* Status bar */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 0.5, fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)' }}>
                <span>10:13</span>
                <span>5G 80%</span>
              </Box>
              {/* App header */}
              <Box sx={{ px: 2, py: 1.25, bgcolor: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="img" src="/logo-icon.png" alt="" sx={{ width: 22, height: 22, filter: 'brightness(0) invert(1)' }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>CruvzMail</Typography>
              </Box>
              {/* Inbox list */}
              <Box sx={{ bgcolor: '#fff', flex: 1, px: 1.5, pt: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#374151', mb: 1, px: 0.5 }}>Inbox</Typography>
                {[
                  { name: 'Sarah Johnson', subject: 'Q2 Report ready for review', time: '10:32', color: '#3b82f6' },
                  { name: 'Dev Team', subject: 'Sprint planning tomorrow', time: '09:15', color: '#22c55e' },
                  { name: 'HR Updates', subject: 'New policy announcement', time: 'Yesterday', color: '#f59e0b' },
                  { name: 'Client Portal', subject: 'Invoice #2847 approved', time: 'Yesterday', color: '#8b5cf6' },
                  { name: 'Marketing', subject: 'Campaign results are in!', time: 'Mon', color: '#ec4899' },
                ].map((em, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, py: 1, borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start' }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: em.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                      <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>{em.name.charAt(0)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1e293b' }}>{em.name}</Typography>
                        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', flexShrink: 0 }}>{em.time}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.6rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{em.subject}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Bottom nav */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 1, bgcolor: '#fff', borderTop: '1px solid #e5e7eb' }}>
                {['📧', '👥', '📅', '⚙️'].map((icon, i) => (
                  <Typography key={i} sx={{ fontSize: '1rem', opacity: i === 0 ? 1 : 0.4 }}>{icon}</Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== FEATURES ===== */}
      <Box id="features" sx={{ py: { xs: 8, md: 12.5 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 30%, #f8fafc 100%)', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 800, height: 800, background: 'radial-gradient(circle, rgba(220,38,38,0.03) 0%, transparent 70%)', borderRadius: '50%' },
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.75, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 100, mb: 2.5 }}>
              <AutoAwesome sx={{ fontSize: 16, color: '#3b82f6' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#3b82f6' }}>More than just email</Typography>
            </Box>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.6rem' }, fontWeight: 800, color: '#0f172a', mb: 1.5, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
              Everything your business needs,<br /><Box component="span" sx={{ background: 'linear-gradient(135deg, #dc2626, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>in one platform</Box>
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', color: '#64748b', maxWidth: 620, mx: 'auto', lineHeight: 1.7 }}>Email, helpdesk, team chat, docs, calendar — all integrated. No more juggling between apps.</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
            {features.map((f, idx) => (
              <Box key={f.title} sx={{
                p: { xs: 3, md: '32px 26px' }, borderRadius: '20px',
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.8)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative', overflow: 'hidden',
                '&::before': {
                  content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                  background: `linear-gradient(90deg, ${f.color}, transparent)`, opacity: 0, transition: 'opacity 0.4s',
                },
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 12px 40px ${(f as any).glow || 'rgba(0,0,0,0.08)'}, 0 4px 16px rgba(0,0,0,0.04)`,
                  border: `1px solid ${f.color}22`,
                  '&::before': { opacity: 1 },
                },
              }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '16px',
                  background: (f as any).bg || f.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  boxShadow: `0 4px 12px ${(f as any).glow || 'rgba(0,0,0,0.06)'}`,
                  '& svg': { fontSize: '1.5rem', color: f.color },
                }}>{f.icon}</Box>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', mb: 1, letterSpacing: '-0.01em' }}>{f.title}</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ===== WHY SECTION ===== */}
      <Box id="why" sx={{ py: { xs: 8, md: 12.5 }, px: { xs: 2, md: 5 }, bgcolor: '#fafbfc' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 5, md: 10 } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, color: '#0f172a', mb: 2, letterSpacing: '-0.02em' }}>Why businesses choose CruvzMail</Typography>
            <Typography sx={{ fontSize: '1.05rem', color: '#64748b', lineHeight: 1.7, mb: 4.5 }}>Built from the ground up for organizations that demand reliability, security, and professional email communication.</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {whyItems.map((w) => (
                <Box key={w.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 2, bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                    <Check sx={{ fontSize: 16, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b', mb: 0.5 }}>{w.title}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.5 }}>{w.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ flex: 1, maxWidth: 500, width: '100%' }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: 5, p: 5, border: '1px solid #e5e7eb', boxShadow: '0 8px 40px rgba(0,0,0,0.06)' }}>
              {metrics.map((m) => (
                <Box key={m.label} sx={{ mb: 3.5, '&:last-child': { mb: 0 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626' }}>{m.value}</Typography>
                  </Box>
                  <Box sx={{ height: 8, bgcolor: '#f1f5f9', borderRadius: 100, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${m.width}%`, background: m.gradient, borderRadius: 100 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== AUTH SECTION ===== */}
      <Box ref={authRef} id="auth" sx={{
        py: { xs: 8, md: 12.5 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(168deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(ellipse at 30% 50%, rgba(220,38,38,0.1) 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(59,130,246,0.08) 0%, transparent 60%)',
        },
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 5, md: 10 }, position: 'relative', zIndex: 1 }}>
          {/* Left info */}
          <Box sx={{ flex: 1, color: '#fff' }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}>Get started with CruvzMail today</Typography>
            <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, mb: 4.5 }}>Create your professional email in minutes. No credit card required for the free plan.</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { icon: <RocketLaunch />, text: 'Set up in under 5 minutes' },
                { icon: <CreditCardOff />, text: 'No credit card required' },
                { icon: <SupportAgent />, text: '24/7 expert support' },
                { icon: <SwapHoriz />, text: 'Easy migration from any provider' },
              ].map((f) => (
                <Box key={f.text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 18, color: '#fca5a5' } }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>{f.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Auth Card */}
          <Box sx={{ width: { xs: '100%', md: 420 }, flexShrink: 0, bgcolor: '#fff', borderRadius: 5, p: 5, boxShadow: '0 25px 80px rgba(0,0,0,0.3)' }}>
            {/* Tabs */}
            <Box sx={{ display: 'flex', mb: 4, borderRadius: 2.5, bgcolor: '#f1f5f9', p: 0.5 }}>
              {(['signin', 'signup'] as const).map((tab) => (
                <Box key={tab} onClick={() => { setAuthTab(tab); setLocalError(''); clearError(); clearTwoFactor(); setOtp(''); }} sx={{
                  flex: 1, py: 1.25, textAlign: 'center', fontSize: '0.9rem', fontWeight: 600, borderRadius: 2, cursor: 'pointer',
                  transition: 'all 0.2s',
                  bgcolor: authTab === tab ? '#fff' : 'transparent',
                  color: authTab === tab ? '#dc2626' : '#64748b',
                  boxShadow: authTab === tab ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                }}>
                  {tab === 'signin' ? 'Sign In' : 'Sign Up'}
                </Box>
              ))}
            </Box>

            {(error || localError) && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error || localError}</Alert>}

            {authTab === 'signin' ? (
              !twoFactorRequired ? (
                <Box component="form" onSubmit={handleSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" autoComplete="email"
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
                    sx={inputSx} />
                  <TextField fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>,
                      endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" sx={{ color: '#94a3b8' }}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                    }}
                    sx={inputSx} />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography component="a" href="/forgot-password" sx={{ fontSize: '0.8rem', color: '#dc2626', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}>Forgot password?</Typography>
                  </Box>
                  <Button type="submit" fullWidth disabled={isLoading} sx={{
                    py: 1.75, borderRadius: 2.5, fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                    color: '#fff', bgcolor: '#dc2626', boxShadow: '0 4px 14px rgba(220,38,38,0.3)', mt: 0.5,
                    '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 6px 20px rgba(220,38,38,0.4)' },
                    '&:disabled': { bgcolor: '#94a3b8' },
                  }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                  </Button>
                  <Typography sx={{ textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8', mt: 1 }}>
                    Don't have an account? <Typography component="span" onClick={() => setAuthTab('signup')} sx={{ color: '#dc2626', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Create one free</Typography>
                  </Typography>
                </Box>
              ) : (
                /* 2FA OTP Form */
                <Box component="form" onSubmit={handleVerifyOtp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="info" icon={twoFactorRequired.otpMethod === 'email' ? <EmailIcon /> : <SmsIcon />} sx={{ borderRadius: 2, bgcolor: '#eff6ff', color: '#1d4ed8' }}>
                    {twoFactorRequired.otpMethod === 'email'
                      ? <>A verification code has been sent to your email{twoFactorRequired.emailHint && ` (${twoFactorRequired.emailHint})`}</>
                      : <>A verification code has been sent to your phone{twoFactorRequired.phoneHint && ` ending in ${twoFactorRequired.phoneHint}`}</>}
                  </Alert>
                  <TextField fullWidth label="Verification Code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit code" autoFocus
                    InputProps={{ startAdornment: <InputAdornment position="start"><SmsIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
                    sx={{ ...inputSx, '& .MuiInputBase-input': { fontSize: '1.2rem', letterSpacing: '0.3em', textAlign: 'center' } }} />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography component="span" onClick={handleResendOtp} sx={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Resend Code</Typography>
                  </Box>
                  <Button type="submit" fullWidth disabled={isLoading || otp.length !== 6} sx={{
                    py: 1.75, borderRadius: 2.5, fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                    color: '#fff', bgcolor: '#dc2626', boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
                    '&:hover': { bgcolor: '#b91c1c' }, '&:disabled': { bgcolor: '#94a3b8' },
                  }}>
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Sign In'}
                  </Button>
                  <Button fullWidth onClick={() => { clearTwoFactor(); setOtp(''); }} sx={{ py: 1, fontSize: '0.9rem', fontWeight: 500, textTransform: 'none', color: '#64748b' }}>
                    ← Back to Login
                  </Button>
                </Box>
              )
            ) : (
              /* Sign Up — redirect to signup wizard */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ fontSize: '0.9rem', color: '#64748b', textAlign: 'center', mb: 1 }}>
                  Start your free account in minutes with our guided setup wizard.
                </Typography>
                <Button fullWidth onClick={() => navigate('/signup')} sx={{
                  py: 1.75, borderRadius: 2.5, fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
                  color: '#fff', bgcolor: '#dc2626', boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
                  '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 6px 20px rgba(220,38,38,0.4)' },
                }}>
                  Create Free Account
                </Button>
                <Typography sx={{ textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8', mt: 1 }}>
                  Already have an account? <Typography component="span" onClick={() => setAuthTab('signin')} sx={{ color: '#dc2626', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Sign in</Typography>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ===== FOOTER ===== */}
      <Box sx={{ bgcolor: '#0a0f1a', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3), rgba(59,130,246,0.3), transparent)' },
      }}>
        {/* Top CTA strip */}
        <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(15,23,42,0) 50%, rgba(59,130,246,0.06) 100%)' }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.02em' }}>Ready to upgrade your business email?</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>Join thousands of businesses already using CruvzMail. Free plan available.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button onClick={() => { setAuthTab('signup'); scrollToAuth(); }} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                boxShadow: '0 4px 20px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #ef4444, #dc2626)', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(220,38,38,0.4)' },
                transition: 'all 0.3s',
              }}>Start Free Trial</Button>
              <Button onClick={() => navigate('/contact')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600, textTransform: 'none',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.3s',
              }}>Contact Sales</Button>
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Main footer content */}
        <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 6, lg: 10 } }}>
            {/* Brand column */}
            <Box sx={{ flex: '0 0 320px', maxWidth: { xs: '100%', lg: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box component="img" src="/Cruvzmail_Footer_logo.png" alt="CruvzMail" sx={{ height: 80, alignSelf: 'flex-start' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2, letterSpacing: '0.02em' }}>Where Business Speaks!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 3 }}>
                Professional email hosting built for modern businesses. Self-hosted, secure, and fully customizable with your brand.
              </Typography>
              {/* App download badges */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <Button href="/CruvzMail-v1.0.apk" download sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'all 0.25s', display: 'flex', alignItems: 'center', gap: 1,
                }} startIcon={<Android sx={{ fontSize: 20 }} />}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>Download on</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Android</Typography>
                  </Box>
                </Button>
                <Box sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5,
                }}>
                  <Apple sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>Coming soon</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1.2 }}>iOS</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Trust badges */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: '256-bit encryption' },
                  { icon: <VerifiedUser sx={{ fontSize: 14 }} />, text: '99.9% uptime' },
                  { icon: <Block sx={{ fontSize: 14 }} />, text: 'Zero ads' },
                ].map((badge) => (
                  <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& svg': { color: 'rgba(255,255,255,0.25)' } }}>
                    {badge.icon}
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>{badge.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Link columns */}
            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 4, md: 3 } }}>
              {[
                { title: 'Product', links: [
                  { label: 'Features', href: '/features' }, { label: 'Pricing', href: '/signup' },
                  { label: 'Enterprise', href: '/enterprise' }, { label: 'Migration', href: '/migration' },
                  { label: 'Security', href: '/features' }, { label: 'Mobile App', href: '/CruvzMail-v1.0.apk' },
                ]},
                { title: 'Solutions', links: [
                  { label: 'Small Business', href: '/signup' }, { label: 'Enterprise', href: '/enterprise' },
                  { label: 'Startups', href: '/signup' }, { label: 'Education', href: '/signup' },
                  { label: 'Healthcare', href: '/signup' }, { label: 'Non-Profit', href: '/signup' },
                ]},
                { title: 'Company', links: [
                  { label: 'About Us', href: '/about' }, { label: 'Blog', href: '/blog' },
                  { label: 'Careers', href: '/careers' }, { label: 'Contact', href: '/contact' },
                  { label: 'Press Kit', href: '/about' }, { label: 'Partners', href: '/contact' },
                ]},
                { title: 'Resources', links: [
                  { label: 'Help Center', href: '/help-center' }, { label: 'Documentation', href: '/documentation' },
                  { label: 'API Reference', href: '/documentation' }, { label: 'System Status', href: '/status' },
                  { label: 'Community', href: '/community' }, { label: 'Webinars', href: '/blog' },
                ]},
              ].map((col) => (
                <Box key={col.title}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5 }}>{col.title}</Typography>
                  {col.links.map((link) => (
                    <Typography
                      key={link.label}
                      component="a"
                      href={link.href}
                      onClick={(e: React.MouseEvent) => { if (!link.href.endsWith('.apk')) { e.preventDefault(); navigate(link.href); } }}
                      sx={{ display: 'block', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', mb: 1.5, transition: 'all 0.2s', '&:hover': { color: '#fca5a5', transform: 'translateX(2px)' } }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Bottom bar */}
        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} CruvzMail by Cruvz Online Shopping India Pvt Ltd. All rights reserved.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
                { label: 'SLA', href: '/terms-of-service' },
                { label: 'Sitemap', href: '/features' },
              ].map((l, i) => (
                <React.Fragment key={l.label}>
                  {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />}
                  <Typography component="a" href={l.href} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(l.href); }} sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>{l.label}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>All systems operational</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
